import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { TemperatureBadge } from "@/components/crm/Badges";
import { fullName, fmtDateTime, isInvalidPhone } from "@/lib/crm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Phone, ArrowUpRight, ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import BulkAssignDialog from "@/components/crm/BulkAssignDialog";

const PAGE_SIZE = 50;
const STATE_KEY = "unassigned_list_state";

function readSaved() {
  try { return JSON.parse(sessionStorage.getItem(STATE_KEY) || "null"); } catch { return null; }
}

export default function UnassignedLeads({ onChange }) {
  const navigate = useNavigate();
  const [saved] = useState(readSaved);
  const reqId = useRef(0);
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(saved?.page || 1);
  const [search, setSearch] = useState(saved?.search || "");
  const [query, setQuery] = useState(saved?.search || "");
  const [selected, setSelected] = useState(() => new Set(saved?.selected || []));
  const [bulkOpen, setBulkOpen] = useState(false);
  const [allFor, setAllFor] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { const t = setTimeout(() => { if (search !== query) { setQuery(search); setPage(1); } }, 300); return () => clearTimeout(t); }, [search, query]);

  const load = useCallback(async () => {
    const my = ++reqId.current;
    const { data: d } = await api.get("/leads/unassigned", { params: { page, page_size: PAGE_SIZE, search: query || undefined } });
    if (my === reqId.current) setData(d);
  }, [page, query]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role === "commerciale" && u.active)));
  }, []);

  const persist = (scrollY) => sessionStorage.setItem(STATE_KEY, JSON.stringify({ page, search: query, selected: [...selected], scrollY }));
  useEffect(() => { persist(window.scrollY); }); // eslint-disable-line react-hooks/exhaustive-deps
  const openLead = (id) => { persist(window.scrollY); navigate(`/leads/${id}`); };

  const refreshAll = () => { setSelected(new Set()); setAllFor(null); load(); onChange && onChange(); };

  const assignOne = async (leadId, userId) => {
    setBusy(true);
    await api.post(`/leads/${leadId}/assign`, { assigned_to: userId });
    toast.success("Lead assegnato");
    setBusy(false);
    setSelected((s) => { const n = new Set(s); n.delete(leadId); return n; });
    load(); onChange && onChange();
  };

  const selectAllResults = async () => {
    setBusy(true);
    const { data: d } = await api.get("/leads/unassigned", { params: { ids_only: true, search: query || undefined } });
    setSelected((s) => new Set([...s, ...d.ids]));
    setAllFor(query);
    setBusy(false);
    toast.success(`${d.ids.length} lead selezionati (tutte le pagine)`);
  };

  if (!data) return null;
  const leads = data.items;
  const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  if (page > pages) { setTimeout(() => setPage(pages), 0); }
  const pageAllSel = leads.length > 0 && leads.every((l) => selected.has(l.id));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const togglePage = () => setSelected((s) => { const n = new Set(s); leads.forEach((l) => (pageAllSel ? n.delete(l.id) : n.add(l.id))); return n; });

  return (
    <div className="rounded-lg border-2 border-violet-500/30 bg-card overflow-hidden" data-testid="unassigned-section">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b bg-violet-500/5 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-violet-500/15 text-violet-600 dark:text-violet-400 grid place-items-center"><UserPlus className="w-4 h-4" /></div>
          <h2 className="font-heading font-bold text-lg">Lead da assegnare</h2>
          <span data-testid="unassigned-count" className="ml-1 px-2 py-0.5 rounded-full bg-violet-500 text-white text-xs font-bold">{data.total}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca nome, telefono, città, campagna…" className="h-8 w-64" data-testid="unassigned-search" />
          {selected.size > 0 && (
            <>
              <span className="text-xs text-muted-foreground" data-testid="unassigned-selected-count">{selected.size} selezionati</span>
              <button onClick={() => { setSelected(new Set()); setAllFor(null); }} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="unassigned-clear-selection"><X className="w-3 h-3" />Deseleziona</button>
              <Button size="sm" onClick={() => setBulkOpen(true)} disabled={busy} data-testid="bulk-assign-btn">Assegna selezionati…</Button>
            </>
          )}
        </div>
      </div>

      {pageAllSel && allFor !== query && data.total > leads.length && (
        <div className="px-4 py-2 text-xs bg-primary/5 border-b flex items-center gap-2" data-testid="select-all-results-banner">
          Selezionati i {leads.length} lead di questa pagina.
          <button onClick={selectAllResults} disabled={busy} className="font-semibold text-primary hover:underline inline-flex items-center gap-1" data-testid="select-all-results">
            {busy && <Loader2 className="w-3 h-3 animate-spin" />}Seleziona tutti i {data.total} risultati
          </button>
        </div>
      )}

      {leads.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground">{query ? "Nessun lead da assegnare corrisponde alla ricerca." : "Nessun lead da assegnare. Tutti i lead hanno un commerciale."}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground text-left">
                <th className="px-3 py-2 w-8"><Checkbox checked={pageAllSel} onCheckedChange={togglePage} data-testid="select-all" /></th>
                <th className="px-3 py-2 font-medium">Lead</th>
                <th className="px-3 py-2 font-medium hidden md:table-cell">Telefono</th>
                <th className="px-3 py-2 font-medium hidden lg:table-cell">Zona</th>
                <th className="px-3 py-2 font-medium hidden lg:table-cell">Interesse</th>
                <th className="px-3 py-2 font-medium hidden xl:table-cell">Fonte / Sorgente</th>
                <th className="px-3 py-2 font-medium hidden xl:table-cell">Campagna</th>
                <th className="px-3 py-2 font-medium hidden md:table-cell">Acquisito</th>
                <th className="px-3 py-2 font-medium text-right">Assegna a</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} data-testid={`unassigned-row-${l.id}`} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="px-3 py-2.5"><Checkbox checked={selected.has(l.id)} onCheckedChange={() => toggle(l.id)} data-testid={`select-${l.id}`} /></td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <TemperatureBadge value={l.temperature} />
                      <button onClick={() => openLead(l.id)} className="font-semibold hover:underline text-left" data-testid={`unassigned-open-${l.id}`}>{fullName(l)}</button>
                      <ArrowUpRight className="w-3 h-3 text-muted-foreground" />
                    </div>
                    <div className="text-xs text-muted-foreground">{l.company}</div>
                  </td>
                  <td className="px-3 py-2.5 hidden md:table-cell">
                    {isInvalidPhone(l.phone) ? <span className="text-amber-600 dark:text-amber-400 inline-flex items-center gap-1 text-xs font-medium" data-testid={`unassigned-invalid-phone-${l.id}`}><Phone className="w-3 h-3" />Non valido: {l.phone}</span> : l.phone ? <a href={`tel:${l.phone}`} className="text-blue-500 hover:underline inline-flex items-center gap-1"><Phone className="w-3 h-3" />{l.phone}</a> : "—"}
                  </td>
                  <td className="px-3 py-2.5 hidden lg:table-cell text-muted-foreground">{[l.city, l.province].filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-3 py-2.5 hidden lg:table-cell text-muted-foreground">{(l.interests || []).join(", ") || "—"}</td>
                  <td className="px-3 py-2.5 hidden xl:table-cell text-muted-foreground text-xs">{l.fonte}<br />{l.sorgente}</td>
                  <td className="px-3 py-2.5 hidden xl:table-cell text-muted-foreground text-xs">{l.campaign || "—"}</td>
                  <td className="px-3 py-2.5 hidden md:table-cell text-muted-foreground text-xs">{fmtDateTime(l.created_at)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <Select value="" onValueChange={(v) => assignOne(l.id, v)}>
                      <SelectTrigger className="h-8 w-40 ml-auto" data-testid={`assign-select-${l.id}`}><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                      <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-t text-xs text-muted-foreground">
        <span data-testid="unassigned-page-info">Pagina {data.page} di {pages} · {data.total} lead</span>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" className="h-7" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="unassigned-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" className="h-7" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="unassigned-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
      <BulkAssignDialog open={bulkOpen} onClose={() => setBulkOpen(false)} leadIds={[...selected]} onDone={refreshAll} />
    </div>
  );
}
